import { AlertTriangle, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Attributes,
  Mode,
  StepComponentProps,
} from "../step-config-file/types";

interface InsightItem {
  label: string | any;
  value: string | number | any;
  uom?: string;
}

const InsightTable = <T extends InsightItem>({
  data,
  title,
  value,
}: {
  data: T[];
  title: string;
  value: string;
}) => (
  <AccordionItem value={value}>
    <AccordionTrigger>{title}</AccordionTrigger>
    <AccordionContent className="flex flex-col h-fit">
      {data.map((item, index) => (
        <div key={index} className="flex items-center justify-between">
          <span className="text-muted-foreground">{item.label}</span>
          <span className="text-sm">
            {typeof item.value === "number"
              ? Number.isInteger(item.value)
                ? item.value
                : item.value.toFixed(2)
              : item.value}{" "}
            {item.uom ?? null}
          </span>
        </div>
      ))}
    </AccordionContent>
  </AccordionItem>
);

interface FeatureCardProps {
  address: string;
  featureDetails: InsightItem[];
  floodDetails: InsightItem[];
  headerOneTitle: string;
  headerTwoTitle: string;
  mode: Mode | undefined;
  onClose: () => void;
}

const FeatureCard = ({
  address,
  featureDetails,
  floodDetails,
  headerOneTitle,
  headerTwoTitle,
  mode,
  onClose,
}: FeatureCardProps) => {
  const floodAlertThreshold = floodDetails.find(
    (x) => x.label === `${mode} Year`,
  )?.value;

  const shouldShowAlert =
    typeof floodAlertThreshold === "number" && floodAlertThreshold > 0;

  return (
    <div className="absolute bottom-4 right-4 top-20 w-80 overflow-auto group-has-data-[collapsible=icon]/sidebar-wrapper:top-16">
      <Card className="flex h-full flex-col overflow-hidden bg-card/95">
        <CardHeader>
          <CardTitle className="flex gap-2">
            <Search className="size-10" />
            {address}
          </CardTitle>
          <CardAction>
            <Button variant="ghost" size="icon-sm" onClick={onClose}>
              <X className="size-4" />
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-2 overflow-auto">
          {shouldShowAlert && (
            <Alert variant="warning">
              <AlertTriangle className="size-4" />
              <AlertTitle>At risk of {mode} year flood</AlertTitle>
            </Alert>
          )}
          <div className="flex-1 overflow-auto">
            <Accordion
              type="multiple"
              defaultValue={["featureDetails", "floodDetails"]}
            >
              <InsightTable
                data={featureDetails}
                title={headerOneTitle}
                value="featureDetails"
              />
              <InsightTable
                data={floodDetails}
                title={headerTwoTitle}
                value="floodDetails"
              />
            </Accordion>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

interface CardContentData {
  featureDetails: InsightItem[];
  floodDetails: InsightItem[];
  headerOne: string;
  headerTwo: string;
}

const contentForCard = (
  attributes: Attributes,
  type: "flood-building-height" | "flood-cadastre" | null,
): CardContentData => {
  if (!attributes) {
    return {
      featureDetails: [],
      floodDetails: [],
      headerOne: "",
      headerTwo: "",
    };
  }
  const properties = attributes.properties;

  if (type === "flood-building-height") {
    const featureDetails = [
      {
        label: "Ground Elevation",
        value: properties.ground_elevation,
        uom: "m",
      },
      {
        label: "First Floor Elevation",
        value: properties.first_floor_elevation,
        uom: "m",
      },
      { label: "Roof Height", value: properties.roof_height, uom: "m" },
      { label: "Estimated Levels", value: properties.estimated_levels },
      {
        label: "Swimming Pool Adjacent",
        value: properties.swimming_pool_adjacent,
      },
      { label: "Solar Panels", value: properties.solar_panel },
      { label: "Planning Zone", value: properties.planning_zone },
      { label: "Building PID", value: properties.building_pid },
      { label: "Area", value: properties.area, uom: "m²" },
      { label: "Basement", value: properties.basement },
      { label: "Garage", value: properties.garage },
    ];
    const floodDetails: InsightItem[] = [2, 5, 10, 20, 50, 100, 500, 1500].map(
      (x) => {
        const value = properties[`flood_height_ari${x}`];
        let modifiedVal = !!value ? value : 0;
        modifiedVal = modifiedVal === -9999 ? 0 : modifiedVal;
        return { label: `${x} Year`, value: modifiedVal, uom: "m" };
      },
    );

    return {
      featureDetails,
      floodDetails,
      headerOne: "Primary Buildings Details",
      headerTwo: "Flood Height",
    };
  }

  if (type === "flood-cadastre") {
    const featureDetails: InsightItem[] = [
      { label: "Parcel ID", value: properties.parcel_id },
      { label: "Lot", value: properties.lot },
      { label: "Plan", value: properties.plan },
      { label: "Title Status", value: properties.title_status },
      { label: "Parcel Type", value: properties.parcel_type },
      { label: "Cadastre PID", value: properties.cadastre_pid },
      { label: "Area", value: properties.area, uom: "m²" },
    ];
    const floodDetails: InsightItem[] = [2, 5, 10, 20, 50, 100, 500, 1500].map(
      (x) => {
        const value = properties[`flood_height_ari${x}`];
        let modifiedVal = !!value ? value : 0;
        modifiedVal = modifiedVal === -9999 ? 0 : modifiedVal;
        return { label: `${x} Year`, value: modifiedVal, uom: "m" };
      },
    );
    return {
      featureDetails,
      floodDetails,
      headerOne: "Primary Cadastre Details",
      headerTwo: "Flood height",
    };
  }
  return {
    featureDetails: [],
    floodDetails: [],
    headerOne: "",
    headerTwo: "",
  };
};

export const FeatureCardComponent = (props: StepComponentProps) => {
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    setClosed(false);
  }, [props.attributes]);

  const mode = props.formState?.mode;

  const cardContent = contentForCard(
    props.attributes,
    props.selectedLayer as "flood-building-height" | "flood-cadastre" | null,
  );
  const populatedAttributes = props.attributes
    ? Object.values(props.attributes.properties).length > 0
    : false;

  if (closed || !props.attributes || !populatedAttributes) {
    return null;
  }

  return (
    <FeatureCard
      address={props.attributes.properties.address}
      featureDetails={cardContent.featureDetails}
      floodDetails={cardContent.floodDetails}
      headerOneTitle={cardContent.headerOne}
      headerTwoTitle={cardContent.headerTwo}
      mode={mode}
      onClose={() => setClosed(true)}
    />
  );
};
